// Medição no GA4 e no Tag Manager (trechos de packages/medicao, injetados pelo vite.config.ts).
// Fora de trucode6.com.br o gtag não existe e nada sai daqui.
// Nunca manda nome, apelido, e-mail, id da conta, código de sala ou de convite.
import { type Ciclo, NOME_DO_CICLO, NOME_DO_PLANO, type PlanoPago, precoCentavos } from '@truco6/planos';
import type { Rota } from './rotas.ts';

type Gtag = (...args: unknown[]) => void;
declare global {
  interface Window {
    gtag?: Gtag;
    dataLayer?: unknown[];
    t6Cookies?: { escolha(): 'sim' | 'nao' | null; aceitar(): void; recusar(): void; abrir(): void };
  }
}

// Da query, só estes parâmetros vão para o GA4 (e os utm_*). Saem code, sessao, fatura e o resto.
const FICAM = new Set(['plano', 'ciclo', 'aba', 'ingresso', 'badge']);

// Endereço sem código de sala, de convite nem parâmetros de login ou pagamento.
export function caminhoLimpo(url: string): string {
  const u = new URL(url, 'https://jogar.trucode6.com.br');
  const caminho = u.pathname.replace(/^\/(s|c|t|assistir|placar)\/[^/]+/, '/$1/:codigo').replace(/^\/sala\/[A-Za-z2-9]{6}\/?$/, '/sala/:codigo').replace(/^\/trios\/(?!novo$)[^/]+/, '/trios/:trio')
    .replace(/^\/jogador\/[^/]+/, '/jogador/:jogador').replace(/^\/comunidade\/[^/]+/, '/comunidade/:codigo')
    .replace(/^\/comunidades\/(?!nova$)[^/]+/, '/comunidades/:comunidade').replace(/^\/campeonato\/[^/]+/, '/campeonato/:campeonato');
  const q = new URLSearchParams();
  for (const [k, v] of u.searchParams) if (FICAM.has(k) || k.startsWith('utm_')) q.append(k, v);
  const s = q.toString();
  return caminho + (s ? '?' + s : '');
}

export const TITULOS: Record<Rota['tela'], string> = {
  inicio: 'Jogar',
  liga: 'Procurando mesa',
  ranking: 'Ranking',
  'nova-comunidade': 'Criar comunidade',
  comunidade: 'Comunidade',
  'convite-comunidade': 'Convite de comunidade',
  'novo-campeonato': 'Criar campeonato',
  campeonato: 'Campeonato',
  conquistas: 'Conquistas',
  missoes: 'Missões',
  colecao: 'Coleção',
  estatisticas: 'Estatísticas',
  marcador: 'Marcador',
  noites: 'Histórico das noites',
  placar: 'Placar ao vivo',
  passe: 'Passe da Temporada',
  trios: 'Trios fixos',
  'novo-trio': 'Criar trio',
  trio: 'Trio fixo',
  'convite-trio': 'Convite de trio',
  sala: 'Sala',
  assistir: 'Assistindo',
  perfil: 'Perfil',
  notificacoes: 'Notificações',
  amigos: 'Amigos',
  configuracoes: 'Configurações',
  jogador: 'Perfil do jogador',
  'nova-sala': 'Criar sala',
  planos: 'Escolha seu plano',
  assinar: 'Trocar de plano',
  'assinatura-ok': 'Assinatura confirmada',
  'meu-plano': 'Meu plano',
  indique: 'Indique e ganhe',
  'como-jogar': 'Como jogar',
  termos: 'Termos de Uso',
  privacidade: 'Política de Privacidade',
  limite: 'Limite diário',
  indicacao: 'Convite de amigo',
  simulado: 'Pagamento simulado',
  loja: 'Loja de Tentos',
  extrato: 'Extrato de Tentos',
  'compra-ok': 'Compra de Tentos',
  'compra-simulada': 'Pagamento simulado',
};

export function medir(evento: string, dados: Record<string, unknown> = {}): void {
  const { gtag, dataLayer } = window;
  if (typeof gtag !== 'function') return;
  gtag('event', evento, dados);
  // Para gatilhos do Tag Manager. O GA4 já recebe pelo gtag: não crie tag do GA4 no GTM para estes eventos.
  dataLayer?.push({ dados: null }, { event: evento, dados });
}

let anterior: string | null = null;

// Uma visita por tela. O endereço limpo vale para todos os eventos seguintes (page_location do gtag).
export function paginaVista(tela: Rota['tela']): void {
  const { gtag } = window;
  if (typeof gtag !== 'function') return;
  const local = location.origin + caminhoLimpo(location.href);
  if (local === anterior) return;
  const ref = anterior ?? (document.referrer.startsWith(location.origin) ? location.origin + caminhoLimpo(document.referrer) : document.referrer);
  gtag('set', { page_location: local, page_title: TITULOS[tela], page_referrer: ref });
  anterior = local;
  medir('page_view');
}

const jaMedidos = new Set<string>();

// Evento que não pode contar duas vezes na mesma aba (recarregar a tela, reconectar à mesa).
export function medirUmaVez(chave: string, evento: string, dados: Record<string, unknown> = {}): void {
  if (typeof window.gtag !== 'function') return;
  const k = 't6:medido:' + chave;
  try {
    if (sessionStorage.getItem(k)) return;
    sessionStorage.setItem(k, '1');
  } catch {
    if (jaMedidos.has(k)) return;
  }
  jaMedidos.add(k);
  medir(evento, dados);
}

// Item de comércio do GA4 para um plano num ciclo: prata_anual, R$ 79,92.
export function itemDoPlano(plano: PlanoPago, ciclo: Ciclo) {
  return { item_id: `${plano}_${ciclo}`, item_name: `${NOME_DO_PLANO[plano]} ${NOME_DO_CICLO[ciclo].toLowerCase()}`, item_category: 'assinatura', price: precoCentavos(plano, ciclo) / 100, quantity: 1 };
}

export function compra(plano: PlanoPago, ciclo: Ciclo, extra: Record<string, unknown> = {}) {
  return { currency: 'BRL', value: precoCentavos(plano, ciclo) / 100, items: [itemDoPlano(plano, ciclo)], ...extra };
}
